import { useState, useEffect, useCallback } from 'react'
import { RefreshCw, Search, AlertTriangle, CheckCircle, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

interface Incident {
  id: string
  severity: 'critical' | 'high' | 'medium'
  title: string
  description: string
  source: string
  timestamp: string
  url?: string
  type: 'cloud' | 'cyber' | 'signal'
}

const SEVERITY_STYLES: Record<string, { dot: string; border: string; bg: string; text: string }> = {
  critical: { dot: 'bg-rose', border: 'border-rose/30', bg: 'bg-rose/10', text: 'text-rose-light' },
  high:     { dot: 'bg-amber', border: 'border-amber/30', bg: 'bg-amber/10', text: 'text-amber-light' },
  medium:   { dot: 'bg-blue', border: 'border-blue/30', bg: 'bg-blue/10', text: 'text-blue-light' },
}

export default function IncidentPage() {
  const [incidents, setIncidents] = useState<Incident[]>([])
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [cloudRes, cyberRes, signalsRes] = await Promise.allSettled([
        api.get('/api/cloud/status'),
        api.get('/api/cyber/feed', { params: { limit: 30 } }),
        api.get('/api/signals/feed', { params: { limit: 30, severity: 'critical' } }),
      ])

      const timeline: Incident[] = []

      // Cloud providers with non-operational status
      if (cloudRes.status === 'fulfilled') {
        const providers = cloudRes.value.data.providers || []
        for (const provider of providers) {
          if (provider.overall_status && provider.overall_status !== 'operational') {
            const affectedServices = (provider.services || [])
              .filter((s: any) => s.status !== 'operational')
              .map((s: any) => s.name)
              .join(', ')

            timeline.push({
              id: `cloud-${provider.slug || provider.name}`,
              severity: provider.overall_status === 'outage' ? 'critical' : 'high',
              title: `${provider.name}: ${provider.overall_status.toUpperCase()}`,
              description: affectedServices ? `Affected services: ${affectedServices}` : `Provider status: ${provider.overall_status}`,
              source: provider.name,
              timestamp: provider.updated_at || provider.last_updated || new Date().toISOString(),
              url: provider.status_url,
              type: 'cloud',
            })
          }

          // Also check individual incidents from provider
          for (const incident of (provider.incidents || [])) {
            timeline.push({
              id: `cloud-inc-${incident.id || incident.title}`,
              severity: incident.impact === 'critical' ? 'critical' : 'high',
              title: `${provider.name}: ${incident.title || incident.name}`,
              description: incident.body || incident.summary || '',
              source: provider.name,
              timestamp: incident.created_at || incident.timestamp || new Date().toISOString(),
              url: incident.url,
              type: 'cloud',
            })
          }
        }
      }

      // Critical CVEs
      if (cyberRes.status === 'fulfilled') {
        const cves = cyberRes.value.data.cves || []
        const alerts = cyberRes.value.data.alerts || []
        for (const cve of cves) {
          if (cve.severity === 'critical' || cve.exploited_in_wild) {
            timeline.push({
              id: `cve-${cve.id}`,
              severity: 'critical',
              title: `${cve.id}: ${(cve.summary || '').slice(0, 120)}`,
              description: cve.summary || '',
              source: 'NVD / CISA',
              timestamp: cve.published || cve.timestamp || '',
              url: cve.url,
              type: 'cyber',
            })
          }
        }
        for (const alert of alerts) {
          if (alert.severity === 'critical') {
            timeline.push({
              id: `alert-${alert.id}`,
              severity: 'critical',
              title: alert.title,
              description: alert.summary || '',
              source: alert.source || 'CISA',
              timestamp: alert.timestamp || '',
              url: alert.url,
              type: 'cyber',
            })
          }
        }
      }

      // Critical signals
      if (signalsRes.status === 'fulfilled') {
        const signals = signalsRes.value.data.signals || signalsRes.value.data.items || signalsRes.value.data || []
        for (const signal of signals) {
          timeline.push({
            id: `signal-${signal.id || signal.title}`,
            severity: (signal.severity || 'high') as any,
            title: signal.title || signal.name || 'Critical Signal',
            description: signal.summary || signal.description || '',
            source: signal.source || 'Signals',
            timestamp: signal.timestamp || signal.published || '',
            url: signal.url,
            type: 'signal',
          })
        }
      }

      // Sort by timestamp descending
      timeline.sort((a, b) =>
        new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime()
      )

      setIncidents(timeline)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 60_000); return () => clearInterval(i) }, [fetchData])

  const allClear = !loading && incidents.length === 0

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <AlertTriangle size={24} className="text-rose" /> Incident Timeline
          </h1>
          <p className="text-sm text-text-secondary mt-1">Active incidents across cloud, cyber, and infrastructure</p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {loading && !incidents.length && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
          <span className="ml-2 text-text-muted text-sm">Checking for active incidents...</span>
        </div>
      )}

      {allClear && (
        <div className="card p-6 border border-emerald/30 bg-emerald/10">
          <div className="flex items-center gap-3">
            <CheckCircle size={24} className="text-emerald" />
            <div>
              <h2 className="font-semibold text-emerald">All Clear</h2>
              <p className="text-sm text-text-secondary mt-0.5">No active incidents detected across cloud providers, cyber feeds, or infrastructure signals.</p>
            </div>
          </div>
        </div>
      )}

      {!allClear && !loading && (
        <div className="grid grid-cols-3 gap-3">
          <div className="stat-card">
            <p className="stat-label">Cloud Incidents</p>
            <p className="stat-value">{incidents.filter(i => i.type === 'cloud').length}</p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Cyber Threats</p>
            <p className="stat-value">{incidents.filter(i => i.type === 'cyber').length}</p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Critical Signals</p>
            <p className="stat-value">{incidents.filter(i => i.type === 'signal').length}</p>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="relative">
        {incidents.length > 0 && (
          <div className="absolute left-[11px] top-2 bottom-2 w-px bg-border" />
        )}
        <div className="space-y-4">
          {incidents.map((incident, idx) => {
            const style = SEVERITY_STYLES[incident.severity] || SEVERITY_STYLES.medium
            return (
              <div key={incident.id || idx} className="flex gap-4 relative">
                {/* Timeline dot */}
                <div className="flex-shrink-0 relative z-10">
                  <div className={clsx('w-[22px] h-[22px] rounded-full border-2 border-surface-base flex items-center justify-center', style.dot)}>
                    <div className="w-2 h-2 rounded-full bg-white/80" />
                  </div>
                </div>
                {/* Card */}
                <div className={clsx('flex-1 card p-4 border', style.border, style.bg)}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={clsx(
                          'text-[10px]',
                          incident.severity === 'critical' ? 'badge-red' : 'badge-amber'
                        )}>
                          {incident.severity.toUpperCase()}
                        </span>
                        <span className={clsx(
                          'text-[10px]',
                          incident.type === 'cloud' ? 'badge-blue' : incident.type === 'cyber' ? 'badge-purple' : 'badge-cyan'
                        )}>
                          {incident.type.toUpperCase()}
                        </span>
                        <span className="text-[10px] text-text-muted">{incident.source}</span>
                        <span className="text-[10px] text-text-muted">&middot;</span>
                        <span className="text-[10px] text-text-muted">{relativeTime(incident.timestamp)}</span>
                      </div>
                      <h3 className="text-sm font-medium text-text-primary leading-snug">{incident.title}</h3>
                      {incident.description && (
                        <p className="text-xs text-text-secondary mt-1 line-clamp-2">{incident.description}</p>
                      )}
                    </div>
                    {incident.url && (
                      <a href={incident.url} target="_blank" rel="noopener" className="text-text-muted hover:text-text-primary flex-shrink-0">
                        <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
